import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { FaBookmark } from 'react-icons/fa';
import { IoNotificationsSharp } from 'react-icons/io5';
import { MdDarkMode, MdLightMode, MdMoreVert, MdSettings } from 'react-icons/md';

import { MsgAction } from '../../store/actions';
import { Storage } from '../../config';
import { DARK_MODE_EVENT, ROUTES, STORAGE_KEYS } from '../../config/constants';
import { useDarkModeValue } from '../../hooks/useDarkModeValue';
import { useIsPhone } from '../../hooks/useMediaQuery';

/**
 * The phone-only overflow menu that sits at the end of a sidebar header.
 *
 * The floating island at the foot of a phone screen carries three destinations —
 * Messages, Calls, Notes. Everything else that used to be crowded into it lives here:
 * Notifications, Saved messages, the theme switch and Settings. Seven 50px slots never
 * fit the 360-390px viewports this app actually runs on, and the list was only going to
 * grow.
 *
 * Rendered from `SidebarBrand`, so every list header gets it without six call sites
 * repeating themselves. It is deliberately NOT in the pane header: on a phone an open
 * conversation covers the list, and the way back is the Back arrow the pane already has —
 * the island stays on screen throughout, so nothing here is ever more than two taps away.
 *
 * Gated on `useIsPhone()` rather than `display: none`, for two reasons: a hidden button
 * is still in the tab order, and the unread fetch below should not run on a desktop that
 * already shows Notifications in its rail.
 */

const ITEMS = [
  {
    key: 'notifications',
    to: ROUTES.NOTIFICATIONS,
    label: 'Notifications',
    Icon: IoNotificationsSharp,
  },
  { key: 'saved', to: ROUTES.SAVED, label: 'Saved messages', Icon: FaBookmark },
];

const isUnder = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

const SidebarOverflow = () => {
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isDarkMode } = useDarkModeValue();
  const [open, setOpen] = useState(false);

  const pathname = location?.pathname ?? '';
  const unread = useSelector((state) => state?.MsgReducer?.notificaiton_unread?.all);
  const unreadCount = Number(unread) > 0 ? Number(unread) : 0;

  /**
   * Keep the unread total fresh.
   *
   * The count already had an action, a saga, an endpoint and a reducer slot — but the
   * only thing dispatching it was the Notifications sidebar itself, so it read 0 until
   * you were already looking at the notifications. A badge that only appears once you no
   * longer need it is not a badge.
   *
   * Refetched on navigation rather than polled, and skipped on the notifications route
   * because that screen fetches it itself (and marks rows read, which changes it). There
   * is no socket event for a new notification, so a notification arriving while the user
   * sits on one screen shows up at their next navigation rather than instantly.
   */
  useEffect(() => {
    if (!isPhone) return;
    if (isUnder(pathname, ROUTES.NOTIFICATIONS)) return;
    dispatch(MsgAction.NotificationsUnreadCount());
  }, [dispatch, isPhone, pathname]);

  // A route change closes the menu. Without this, tapping a destination left the menu
  // mounted over the screen it had just navigated to.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!isPhone) return null;

  const toggleTheme = () => {
    Storage.writeJSON(STORAGE_KEYS.DARK_MODE, !isDarkMode);
    window.dispatchEvent(new Event(DARK_MODE_EVENT));
    setOpen(false);
  };

  return (
    <div className="sidebar-overflow">
      <button
        type="button"
        className="sidebar-overflow-btn"
        onClick={() => setOpen((v) => !v)}
        title="More"
        aria-label={unreadCount ? `More — ${unreadCount} unread notifications` : 'More'}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <MdMoreVert aria-hidden="true" />
        {/* A dot rather than a number: the exact count is on the row inside, and this
            button is 34px of a header that already carries a wordmark and a compose
            action. */}
        {unreadCount > 0 && <span className="sidebar-overflow-dot" aria-hidden="true" />}
      </button>

      {open && (
        <>
          <div
            className="sidebar-overflow-scrim"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="sidebar-overflow-menu" role="menu">
            {ITEMS.map(({ key, to, label, Icon }) => (
              <button
                key={key}
                type="button"
                role="menuitem"
                className={`sidebar-overflow-item${isUnder(pathname, to) ? ' is-active' : ''}`}
                onClick={() => navigate(to)}
              >
                <Icon aria-hidden="true" />
                {label}
                {key === 'notifications' && unreadCount > 0 && (
                  <span className="sidebar-overflow-count">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            ))}

            <button
              type="button"
              role="menuitem"
              className="sidebar-overflow-item"
              onClick={toggleTheme}
            >
              {isDarkMode ? <MdLightMode aria-hidden="true" /> : <MdDarkMode aria-hidden="true" />}
              {isDarkMode ? 'Light theme' : 'Dark theme'}
            </button>

            <button
              type="button"
              role="menuitem"
              className={`sidebar-overflow-item${isUnder(pathname, ROUTES.SETTINGS) ? ' is-active' : ''}`}
              onClick={() => navigate(ROUTES.SETTINGS_PROFILE)}
            >
              <MdSettings aria-hidden="true" />
              Settings
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SidebarOverflow;
